/* tslint:disable:component-selector import-spacing no-trailing-whitespace no-inferrable-types */

import {
  Component,
  ViewChildren,
  QueryList,
  AfterViewInit
}                              from '@angular/core';

import { Logger            }   from '@/util/logger';


import {UserLogComponent}      from '@/components/user.log.component';
import {UserLogViewerMetadata} from '@/screens/ide/user.log.viewer.metadata';
import {DocumentWindowManager} from '@/screens/ide/document.window.manager';
import {WindowType}            from '@/screens/ide/window';
import {UserLogViewerWindow}   from '@/screens/ide/user.log.viewer.window';
import {DocumentWindow}        from '@/screens/ide/document.window';

import {BehaviorSubject}       from 'rxjs';
import {Subscription}          from 'rxjs';

import 'gasparesganga-jquery-loading-overlay'; /* https://gasparesganga.com/labs/jquery-loading-overlay/ */


/**
 *
 * @class UserLogViewerComponent - this component manages 1 or more UserLogComponent's, i.e. 1 or more
 * user log trees.  Each tree is in its own stretchable panel, and can be loaded up with its own data;
 * allowing users to compare user log trees side by side.
 *
 * @note this class uses dynamic component creation to host an Angular component inside a non-Angular
 * object (i.e. DocumentWindow).
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2021
 *
 */

@Component({
  selector: 'user-log-viewer-component',
  template: `

    <div class="userlogviewerheader">
      <label class="gui-button anchorwindowbutton" [class.anchored]="anchored">
        <i (click)="toggleAnchor()" class="fa fa-anchor" aria-hidden="true"></i>
      </label>
    </div>

    <div class="userlogviewerbody">
      <p-splitter [style]="{'height': '100%', 'width': '100%'}"  [panelSizes]="[80,10,10]" layout="horizontal">

        <ng-template pTemplate>
          <user-log-component uuidparent="{{u}}-1" order="1"></user-log-component>
        </ng-template>

        <ng-template pTemplate>
          <user-log-component uuidparent="{{u}}-2" order="2"></user-log-component>
        </ng-template>

        <ng-template pTemplate>
          <user-log-component uuidparent="{{u}}-3" order="3"></user-log-component>
        </ng-template>

      </p-splitter>
    </div>
  `
})

export class UserLogViewerComponent implements AfterViewInit {

  /* for uniq'ifying our drop #id's */

  public static  instanceNum: number    = 0;
  public u: number = UserLogViewerComponent.instanceNum;

  /* the error feedback if required */
  public       errorLine: string        = '';

  /**
   *  bubble up a 'ready' event once all the children are ready, so the parent
   *  window knows it can safely load in a tree.
   */
  public ready                          = new BehaviorSubject<boolean>(false);

  /** toggle flag for this window being the anchor (default) window for this kind of window */
  public anchored: boolean              = false;

  /** the document manager controller; where we register for anchoring etc. */
  public manager: DocumentWindowManager = null;

  public window: UserLogViewerWindow    = null;

  /** the individual user log tree's that we can use. */
  @ViewChildren(UserLogComponent) children!: QueryList<UserLogComponent>;
  public kids: UserLogComponent[] = [];

  /** the observable we use to know when some other window has taken the anchor */
  private anchorSub: Subscription;

  /** standard constructor */
  constructor(
  ) {


    Logger.debug('[userlogviewer] dynamically creating...');

  }

  ngAfterViewInit(): void {

    /* - - - - - -  add in drag n' drop behaviors */

    /* get the scenario drop box */
    const          u: number = UserLogViewerComponent.instanceNum;
    const instanceId: string = `ulv_${this.u}`;

    Logger.debug(`[userlogviewer] instance init: ulv_${this.u} ...`);

    /* make sure the children are ordered */

    this.kids = this.children.toArray();
    this.kids.sort((a: UserLogComponent, b: UserLogComponent) => {
      return a.order - b.order
    });

    Logger.debug(`[userlogviewer] children: `, this.kids);

    /* before we can do anything we need to establish which viewer is for this component ... */

    UserLogViewerComponent.instanceNum++;

    setTimeout( () => {

      /* tell the window enclosing this component, its ready for use! */

      this.ready.next(true);

    }, 50);

  }

  /** both parent window and this component are ready, so any final setup */
  public finalize() {

    Logger.debug(`[userlogviewer] ulv_${this.u} finalizing...`);

    let uvc: UserLogViewerComponent = this;

    uvc.anchorSub = uvc.manager.anchorSub.subscribe((anchor: {kind: WindowType, window: DocumentWindow}) => {

      if (anchor.kind == WindowType.None) {

        return ;
      }

      if (anchor.kind != uvc.window.type) {

        /* can ignore not our kind of window */

        return ;
      }

      /* null means our group anchor was cleared, otherwise one of the windows took the anchor */

      if (anchor.window != null) {

        if (anchor.window.id() == uvc.window.id()) {

          /* can ignore self */

          return ;
        }

      }

      /* either cleared or another window took anchor, we have to set anchored to false */

      Logger.debug(`[userlogviewer] ulv_${this.u} cleared / other window took anchor.`);

      this.anchored = false;
    });

  }

  /** windows than can act as an anchor (default) window for their group/kind can "drop anchor" */
  public dropAnchor() {

    if (this.anchored == true) {

      return;
    }

    Logger.debug(`[userlogviewer] ulv_${this.u} dropping anchor`);

    /* tell the doc manager we're the default window of this kind */

    this.manager.dropAnchor(WindowType.UserLogViewer, this.window)
    this.anchored = true;
  }

  /** opposite of dropping anchor, group/kind of windows will not have an anchor unitl another one is dropped. */
  public raiseAnchor() {

    if (this.anchored == false) {

      return;
    }

    /* tell the doc manager there is no default window of this kind */

    Logger.debug(`[userlogviewer] ulv_${this.u} raising anchor`);

    this.manager.raiseAnchor(WindowType.UserLogViewer);

    this.anchored = false;
  }

  public toggleAnchor() {

    if (this.anchored == true) {

      this.raiseAnchor();

    } else {

      this.dropAnchor();
    }

  }

  /** check for geometry updates and update children as needed */
  public resize() {

    /*

    TODO:

     */
  }

  /** append a tree, or reuse an existing one, either way, make the train/dp visible if its not already visible. */
  public refocus(meta: UserLogViewerMetadata, append: boolean = false) {

    Logger.debug(`[userlogviewer] ulv_${this.u} refocusing [${append}]: `, meta);

    const N: number = this.kids.length;

    /*
     * pick a child user log tree to use; if appending, pick an empty one or replace
     * the last one.  If not appending, find the first one with the same collection
     * name and replace it, otherwise append.
     *
     */

    if (!append) {

      /*
       * try to replace, which means replace the tree with the same collection name,
       * or use an empty slot.
       *
       */

      const replace: UserLogComponent = this.kids.find(x => x.collection == meta.collection);

      if (replace) {

        /*
         * load the tree into this one, its the same collection, but it might be focusing on
         * a different train/decision point.
         *
         */

        replace.load(meta);

      } else {

        const empty:  UserLogComponent = this.kids.find((x) => {
          console.log("find: x: ", x, " empty: ", x.empty());
            return x.empty();
        });

        if (empty) {

          /* this slot doesn't have a filled in tree, just use it. */

          empty.load(meta);

        } else {

          /*
           * there is no tree for this collection already, and no empty slot we
           * can use, so replace the last one in the list.
           *
           */

          this.kids[this.kids.length - 1].load(meta);
        }
      }

      return ;
    }

    /*
     * if we get this far, we are now attempting to append, either use an empty
     * slot, or replace the last one.
     *
     */

    const empty:  UserLogComponent = this.kids.find(x => x.empty());

    if (empty) {

      /* this slot doesn't have a filled in tree, just use it. */

      empty.load(meta);

      return;
    }

    this.kids[this.kids.length - 1].load(meta);
  }

  /** clean up when closing the window */
  public close() {

    Logger.debug('[userlogviewer] ulv_${this.u} is closing.');

    this.anchorSub.unsubscribe();

  }


}
